/**
 * Pure helpers behind the back-office Product Details and Variant Details
 * screens: stock status, stock value and the monthly sales response.
 */
import type { BackofficeProduct } from '../redux/backoffice/productsSlice';
import type { ProductVariant } from '../redux/productSlice';
import {
  formatNumberDecimal,
  roundMoney,
  roundToNumericPrecision,
} from './libs';
import { getVariantAttrLabel } from './productDisplayName';
import { isServiceProduct } from './productKind';

/** `out`: 0 or less in stock. `low`: above 0 and at or below the minimum set. */
export type StockStatus = 'out' | 'low' | 'ok';

/** Number from a stored field; null when it is missing or not a number. */
export const toFiniteNumber = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** True for a variant removed in the product form. */
export const isDeletedVariant = (variant: ProductVariant): boolean =>
  // The server stores `bp_variant_deleted` and sends removed variants back in
  // the owner product list; `bp_variant_is_deleted` is the flag the form sends.
  (variant as any)?.bp_variant_deleted === true ||
  (variant as any)?.bp_variant_is_deleted === true;

/** The product's variants, without the ones removed in the product form. */
export const getSavedVariants = (
  product: BackofficeProduct | null | undefined,
): ProductVariant[] => {
  const variants = product?.bp_product_variants;
  return Array.isArray(variants)
    ? variants.filter(variant => variant && !isDeletedVariant(variant))
    : [];
};

/**
 * The flag alone, as in utils/variantHelpers. A product whose variants were
 * all removed stays a variant product (prices and stock live on variants).
 */
export const isVariantProduct = (
  product: BackofficeProduct | null | undefined,
): boolean => Boolean(product?.bp_product_has_variants);

export const getStockStatus = (
  quantity: unknown,
  minimum: unknown,
): StockStatus => {
  const qty = toFiniteNumber(quantity) ?? 0;
  if (qty <= 0) return 'out';
  const min = toFiniteNumber(minimum);
  if (min != null && min > 0 && qty <= min) return 'low';
  return 'ok';
};

export type ProductStockFlags = {
  /** Worst status across the product or its variants. */
  status: StockStatus;
  /** Variant labels out of stock / low on stock; empty for single products. */
  outLabels: string[];
  lowLabels: string[];
  /** The product's own minimum, for the "Low stock · min 5" note. */
  minimum: number | null;
};

/**
 * Stock status for an Inventory row. A product with variants is out or low
 * when any of its saved variants is, and out when none are left (as the
 * server's Out of stock filter counts it). A service keeps no stock, so it
 * is never out or low.
 */
export const getProductStockFlags = (
  product: BackofficeProduct | null | undefined,
): ProductStockFlags => {
  if (!product || isServiceProduct(product))
    return { status: 'ok', outLabels: [], lowLabels: [], minimum: null };
  if (isVariantProduct(product)) {
    const variants = getSavedVariants(product);
    if (!variants.length)
      return { status: 'out', outLabels: [], lowLabels: [], minimum: null };
    const outLabels: string[] = [];
    const lowLabels: string[] = [];
    variants.forEach((variant, index) => {
      const status = getVariantStockStatus(variant);
      if (status === 'out') outLabels.push(getVariantLabel(variant, index));
      if (status === 'low') lowLabels.push(getVariantLabel(variant, index));
    });
    return {
      status: outLabels.length ? 'out' : lowLabels.length ? 'low' : 'ok',
      outLabels,
      lowLabels,
      minimum: null,
    };
  }
  const minimum = toFiniteNumber(product.bp_minimum_stock_level);
  return {
    status: getStockStatus(product.bp_product_quantity, minimum),
    outLabels: [],
    lowLabels: [],
    minimum: minimum != null && minimum > 0 ? minimum : null,
  };
};

/**
 * The red stock notes for a product: "Out of stock", "Low stock",
 * "Out of stock: 1L", "Low stock: 500ml". `showMinimum` adds the product's
 * own minimum ("Low stock · min 5") where nothing else on screen shows it.
 */
export const getStockNotes = (
  product: BackofficeProduct | null | undefined,
  { showMinimum = false }: { showMinimum?: boolean } = {},
): string[] => {
  const flags = getProductStockFlags(product);
  if (isVariantProduct(product)) {
    // No variants left
    if (flags.status === 'out' && !flags.outLabels.length)
      return ['Out of stock'];
    const notes: string[] = [];
    if (flags.outLabels.length)
      notes.push(`Out of stock: ${flags.outLabels.join(', ')}`);
    if (flags.lowLabels.length)
      notes.push(`Low stock: ${flags.lowLabels.join(', ')}`);
    return notes;
  }
  if (flags.status === 'out') return ['Out of stock'];
  if (flags.status === 'low') {
    return [
      showMinimum && flags.minimum != null
        ? `Low stock · min ${formatNumberDecimal(flags.minimum)}`
        : 'Low stock',
    ];
  }
  return [];
};

export const getVariantStockStatus = (variant: ProductVariant): StockStatus =>
  getStockStatus(
    variant?.bp_variant_quantity,
    variant?.bp_variant_minimum_stock_level,
  );

/** Same fallback as the Inventory list's low-variant note. */
export const getVariantLabel = (
  variant: ProductVariant | null | undefined,
  index: number,
): string => getVariantAttrLabel(variant) || `Variant ${index + 1}`;

/** Sum of every variant's stock, as the Inventory list shows it. */
export const getVariantStockTotal = (
  product: BackofficeProduct | null | undefined,
): number =>
  roundToNumericPrecision(
    getSavedVariants(product).reduce(
      (sum, variant) =>
        sum + (toFiniteNumber(variant?.bp_variant_quantity) ?? 0),
      0,
    ),
  );

/**
 * Stock × price. Nothing in stock is worth 0; a missing price is unknown
 * (null), never 0.
 */
export const getStockValue = (
  quantity: unknown,
  price: unknown,
): number | null => {
  const unitPrice = toFiniteNumber(price);
  if (unitPrice == null) return null;
  const qty = toFiniteNumber(quantity) ?? 0;
  return qty > 0 ? roundMoney(qty * unitPrice) : 0;
};

export type StockValue = { selling: number | null; buying: number | null };

export const getVariantStockValue = (variant: ProductVariant): StockValue => ({
  selling: getStockValue(
    variant?.bp_variant_quantity,
    variant?.bp_variant_selling_price,
  ),
  buying: getStockValue(
    variant?.bp_variant_quantity,
    variant?.bp_variant_buying_price,
  ),
});

/** A product with variants adds its variants up; one unknown price makes the total unknown. */
export const getProductStockValue = (
  product: BackofficeProduct | null | undefined,
): StockValue => {
  if (!product) return { selling: null, buying: null };
  if (!isVariantProduct(product)) {
    return {
      selling: getStockValue(
        product.bp_product_quantity,
        product.bp_product_selling_price,
      ),
      buying: getStockValue(
        product.bp_product_quantity,
        product.bp_product_buying_price,
      ),
    };
  }
  let selling: number | null = 0;
  let buying: number | null = 0;
  for (const variant of getSavedVariants(product)) {
    const value = getVariantStockValue(variant);
    selling =
      selling == null || value.selling == null ? null : selling + value.selling;
    buying =
      buying == null || value.buying == null ? null : buying + value.buying;
  }
  return {
    selling: selling == null ? null : roundMoney(selling),
    buying: buying == null ? null : roundMoney(buying),
  };
};

export type MonthSales = {
  /** Money received for the month. */
  sales: number;
  /** Items sold (products sold per item). */
  quantity: number;
  /** Units of mass sold (products sold per unit, like Kg). */
  mass: number;
};

export type MonthlySalesResult =
  | { kind: 'ok'; months: MonthSales[] }
  /** Asked for one variant, but the server answered for the whole product. */
  | { kind: 'unsupported' };

/**
 * Reads `/bp_get_product_monthly_sales_report_information`. Returns null for
 * anything but an explicit success so the screen keeps its "—" placeholders.
 */
export const parseProductMonthlySales = (
  res: any,
  variantId?: string,
): MonthlySalesResult | null => {
  if (res?.status !== 200) return null;
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  if (body.status != null && Number(body.status) !== 200) return null;
  if (!Array.isArray(body.data)) return null;
  if (variantId && String(body.bp_variant_id ?? '') !== String(variantId)) {
    return { kind: 'unsupported' };
  }

  const months: MonthSales[] = Array.from({ length: 12 }, () => ({
    sales: 0,
    quantity: 0,
    mass: 0,
  }));
  body.data.forEach((entry: any, index: number) => {
    // Each entry is `{ "<month 1-12>": { totalSales, totalQuantity, totalMass } }`.
    let monthIndex = index;
    let values = entry;
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      const keys = Object.keys(entry);
      if (keys.length === 1 && Number.isFinite(Number(keys[0]))) {
        monthIndex = Number(keys[0]) - 1;
        values = entry[keys[0]];
      }
    }
    if (monthIndex < 0 || monthIndex > 11) return;
    months[monthIndex] = {
      sales: toFiniteNumber(values?.totalSales) ?? 0,
      quantity: toFiniteNumber(values?.totalQuantity) ?? 0,
      mass: toFiniteNumber(values?.totalMass) ?? 0,
    };
  });
  return { kind: 'ok', months };
};

export const sumMonthlySales = (months: MonthSales[]): MonthSales => {
  const total = months.reduce(
    (sum, month) => ({
      sales: sum.sales + month.sales,
      quantity: sum.quantity + month.quantity,
      mass: sum.mass + month.mass,
    }),
    { sales: 0, quantity: 0, mass: 0 },
  );
  return {
    sales: roundMoney(total.sales),
    quantity: roundToNumericPrecision(total.quantity),
    mass: roundToNumericPrecision(total.mass),
  };
};
